import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map } from 'rxjs';
import { IsAuthenticatedUseCase, GetAuthStateUseCase } from '@auth';
import { AppConfigService } from '../config/app-config.service';
import { BrowserNavigation, LoggerProvider } from '@shared';
import { SessionExpiredDialogService } from '../services/session-expired-dialog.service';
import { EIDCProvider } from '../providers/oidc/eidc.provider';
import { EidAuthStateAdapter } from '../providers/oidc/eid-auth-state.adapter';

/**
 * Authentication guard to protect routes requiring user authentication.
 * - Local environment: redirects to /local-login
 * - Non-local environments: allows access (EID handles auth via initSession in app initializer)
 *
 * After confirming the user is authenticated, calls GET /users/me to verify the user
 * has a valid tenant. A 401 or 404 response means no agency is assigned → redirects
 * to /no-agency. The /me call also updates the user profile in AuthState.
 *
 * Note: The OIDC authorize() call is handled by EIDCProvider.initSession() during app startup,
 * NOT in the guard. This prevents infinite redirect loops when CSP blocks the OIDC discovery endpoint.
 */
export const authGuard: CanActivateFn = () => {
  const isAuthenticatedUseCase = inject(IsAuthenticatedUseCase);
  const getAuthStateUseCase = inject(GetAuthStateUseCase);
  const configService = inject(AppConfigService);
  const browserNavigation = inject(BrowserNavigation);
  const sessionExpiredDialog = inject(SessionExpiredDialogService);
  const eidcProvider = inject(EIDCProvider);
  const eidAuthStateAdapter = inject(EidAuthStateAdapter);
  const router = inject(Router);
  const logger = inject(LoggerProvider).createScoped('AuthGuard');

  if (!configService.isLocal()) {
    if (isAuthenticatedUseCase.execute() || eidcProvider.isAuthenticated()) {
      return eidAuthStateAdapter.checkUserAccess().pipe(
        map((hasAccess) => (hasAccess ? true : router.createUrlTree(['/no-agency']))),
      );
    }

    return false;
  }

  // Local environment authentication checks
  if (!isAuthenticatedUseCase.execute()) {
    // Dialog already open or already shown this page load — fall back to hard redirect
    if (sessionExpiredDialog.isDialogOpen() || sessionExpiredDialog.wasShown()) {
      browserNavigation.navigateToUrl(configService.loginPath);
      return false;
    }

    // user() is non-null when a session existed but the token expired — _user signal is
    // initialized from localStorage before getValidAccessToken() clears the storage entry
    const authState = getAuthStateUseCase.execute();
    if (authState.user() !== null) {
      logger.warn('Session expired detected - user exists but no valid token');
      sessionExpiredDialog.show();
      return false;
    }

    // No prior session — redirect straight to login
    logger.info('No authentication found, redirecting to login');
    browserNavigation.navigateToUrl(configService.loginPath);
    return false;
  }

  return eidAuthStateAdapter.checkUserAccess().pipe(
    map((hasAccess) => (hasAccess ? true : router.createUrlTree(['/no-agency']))),
  );
};
